import React, { useEffect, useState } from "react";
import {
  Container, Card, CardBody, Row, Col, Label, Input, Button, Spinner,
  FormGroup, Alert, Table, Badge,
  Nav, NavItem, NavLink, TabContent, TabPane,
  Modal, ModalHeader, ModalBody, ModalFooter,
} from "reactstrap";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  getPropertyById, updateProperty, getTenantsByProperty,
  updateTenant,
  addTenantByEmail, removePropertyTenantEmail, getTicketsByPmUid,
  createLease,
  deleteProperty, restoreProperty,
  addTenantToLease,
  getApplicationsByProperty, updateApplicationStatus,
  createListing, getListingsByProperty, syncPublicListings, updateListing, deleteListing,
  getListingFeedbackByProperty,
  sharePropertyAccess, revokePropertyAccess, getPropertyAccessList,
  PROPERTY_ACCESS_VIEWER, PROPERTY_ACCESS_EDITOR,
  getLeasesByOwner,
  updateLease,
  getPaymentsByLease,
  recordManualPayment,
  updateManualPayment,
  deleteManualPayment,
  checkAndNotifyLateRent,
  softDeleteLease,
} from "../../../helpers/firestoreService";
import { getListingImageUrl, uploadListingImage } from "../../../helpers/storageService";
import { getStripeAccountStatus } from "../../../helpers/paymentsApi";
import { paymentSummaryLabel } from "../../../helpers/utils";
import PropertyChat from "../../../components/Common/PropertyChat";
import InspectionsPanel from "../../../components/Common/InspectionsPanel";
import MonthPicker from "../../../components/Common/MonthPicker";
import ScreeningReportPanel from "../Applications/ScreeningReportPanel";
import { useAuthUser } from "../../../hooks/AuthProvider";
import { getListingDisplayTitle } from "../../../helpers/listingTitle";

// Categories mirrored from RentalApplicationPage.jsx's NEARBY_TYPES — computed
// once here at listing-creation time and cached on the listing doc so the
// public application page never has to call the Places API itself.
const NEARBY_SEARCH_TYPES = ["school", "restaurant", "grocery_or_supermarket", "transit_station", "gym", "park"];
const FEEDBACK_TOPIC_LABELS = {
  photos: "More photos",
  fees: "Fees",
  pet_policy: "Pet policy",
  tour_info: "Tour info",
  application_process: "Application process",
  neighborhood: "Neighborhood",
};

const FeedbackStars = ({ rating }) => (
  <span className="d-inline-flex gap-1" aria-label={`${rating} out of 5 stars`}>
    {[1, 2, 3, 4, 5].map((star) => (
      <i
        key={star}
        className="bx bxs-star"
        style={{ color: star <= rating ? "#f1b44c" : "#cbd5e1", fontSize: "1rem" }}
      />
    ))}
  </span>
);

const waitForGoogleMaps = () => new Promise((resolve, reject) => {
  if (window.google?.maps?.places) { resolve(); return; }
  let attempts = 0;
  const t = setInterval(() => {
    attempts += 1;
    if (window.google?.maps?.places) { clearInterval(t); resolve(); }
    else if (attempts > 40) { clearInterval(t); reject(new Error("Google Maps failed to load")); }
  }, 250);
});

const geocodeAddress = (address) => new Promise((resolve, reject) => {
  new window.google.maps.Geocoder().geocode({ address }, (results, status) => {
    if (status === "OK" && results[0]) resolve(results[0].geometry.location);
    else reject(new Error(`Geocode failed: ${status}`));
  });
});

const nearbySearch = (service, location, type) => new Promise((resolve) => {
  service.nearbySearch({ location, radius: 3000, type }, (results, status) => {
    if (status === window.google.maps.places.PlacesServiceStatus.OK && results) {
      resolve(results.filter((result) => Number(result.rating) > 4).slice(0, 5).map((r) => ({
        name: r.name || "",
        vicinity: r.vicinity || "",
        rating: r.rating || null,
        lat: r.geometry?.location?.lat ? r.geometry.location.lat() : null,
        lng: r.geometry?.location?.lng ? r.geometry.location.lng() : null,
      })));
    } else {
      resolve([]);
    }
  });
});

/** One-time nearby-places lookup for a new listing. Returns null on any failure. */
async function computeNearbyPlaces(address) {
  if (!address) return null;
  await waitForGoogleMaps();
  const location = await geocodeAddress(address);
  const service = new window.google.maps.places.PlacesService(document.createElement("div"));
  const entries = await Promise.all(
    NEARBY_SEARCH_TYPES.map(async (type) => [type, await nearbySearch(service, location, type)])
  );
  const results = Object.fromEntries(entries);
  results._center = { lat: location.lat(), lng: location.lng() };
  return results;
}

const PropertyDetail = () => {
  document.title = "Property Details | Support Portal";

  const { propertyId } = useParams();
  const navigate = useNavigate();
  const user = useAuthUser();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [address, setAddress] = useState("");
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Tenants assigned to this property
  const [tenants, setTenants] = useState([]);
  const [tenantLoading, setTenantLoading] = useState(true);

  // Add tenant modal — email only, the person accepts the invite on sign-in
  const [addTenantModal, setAddTenantModal] = useState(false);
  const [tenantSaving, setTenantSaving] = useState(false);
  const [tenantError, setTenantError] = useState("");
  const [newTenantEmail, setNewTenantEmail] = useState("");

  // Remove tenant modal
  const [removeTenantModal, setRemoveTenantModal] = useState(false);
  const [removeTenantTarget, setRemoveTenantTarget] = useState(null);

  // Tickets
  const [tickets, setTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(true);

  // Lease
  const [activeLease, setActiveLease] = useState(null);
  const [leaseLoading, setLeaseLoading] = useState(true);
  const [leasePayments, setLeasePayments] = useState([]);
  const [createLeaseModal, setCreateLeaseModal] = useState(false);
  const [leaseMode, setLeaseMode] = useState(null); // null = pick mode, "simple" = form
  const [editingLease, setEditingLease] = useState(false); // true = updating activeLease instead of creating a new one
  const [leaseForm, setLeaseForm] = useState({
    startDate: "",
    endDate: "",
    monthlyRent: "",
    securityDeposit: "",
    rentDueDay: "1",
    firstPaymentDate: "",
    notes: "",
  });
  const [leaseSaving, setLeaseSaving] = useState(false);
  const [leaseError, setLeaseError] = useState("");

  // Record a manual (off-platform) payment — e.g. cash/check, or backfilling
  // a past-dated payment to test/demo late-rent detection.
  const [recordPaymentModal, setRecordPaymentModal] = useState(false);
  const [editingPaymentId, setEditingPaymentId] = useState(null); // null = creating a new record
  const [recordPaymentForm, setRecordPaymentForm] = useState({
    tenantId: "", type: "rent", month: "", amount: "", paidAt: "", note: "",
  });
  const [recordPaymentSaving, setRecordPaymentSaving] = useState(false);
  const [recordPaymentError, setRecordPaymentError] = useState("");

  // Delete a manual (off-platform) payment record — Stripe-verified charges
  // are immutable and never show a delete option in the first place.
  const [deletePaymentModal, setDeletePaymentModal] = useState(false);
  const [deletePaymentTarget, setDeletePaymentTarget] = useState(null);
  const [deletePaymentSaving, setDeletePaymentSaving] = useState(false);

  // People this property is shared with — [{ email, role }]
  const [accessList, setAccessList] = useState([]);
  const [accessModal, setAccessModal] = useState(false);
  const [accessEmail, setAccessEmail] = useState("");
  const [accessRole, setAccessRole] = useState(PROPERTY_ACCESS_VIEWER);
  const [accessSaving, setAccessSaving] = useState(false);
  const [accessError, setAccessError] = useState("");

  // Revoke access confirm modal
  const [revokeModal, setRevokeModal] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState(null);

  // Applications
  const [applications, setApplications] = useState([]);
  const [appsLoading, setAppsLoading] = useState(true);
  const [appSearch, setAppSearch] = useState("");
  const [selectedApp, setSelectedApp] = useState(null);
  const [appModal, setAppModal] = useState(false);
  const [appStatusError, setAppStatusError] = useState("");

  // Rental application links (listings)
  const [listingModal, setListingModal] = useState(false);
  const [listings, setListings] = useState([]);
  const [listingsLoading, setListingsLoading] = useState(false);
  const [listingSaving, setListingSaving] = useState(false);
  const [listingError, setListingError] = useState("");
  const [newListingId, setNewListingId] = useState("");
  const [copiedLink, setCopiedLink] = useState("");
  // Which application link is open inside the Applications modal (null = link list)
  const [selectedListingId, setSelectedListingId] = useState(null);
  const [listingBusy, setListingBusy] = useState("");
  const [confirmDeleteListing, setConfirmDeleteListing] = useState("");
  const [editingListingId, setEditingListingId] = useState("");
  const [listingFeedback, setListingFeedback] = useState([]);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [listingForm, setListingForm] = useState({
    title: "",
    monthlyRent: "",
    deposit: "",
    bedrooms: "",
    bathrooms: "",
    sqft: "",
    availableDate: "",
    description: "",
    petPolicy: "no-pets",
    leaseTerm: "month-to-month",
  });
  // Existing and newly staged photos share one ordered list. Position zero is
  // the cover image used by the public listing.
  const [listingImages, setListingImages] = useState([]);
  const [listingImagesUploading, setListingImagesUploading] = useState(false);

  // Stripe payout status — an application link can't be created until this
  // is set up (see openListingModal).
  const [stripeStatus, setStripeStatus] = useState(null);

  // Tenants card tabs
  const [tenantTab, setTenantTab] = useState("tenants"); // "tenants" | "chat"

  const [leaseOptionsModal, setLeaseOptionsModal] = useState(false);
  const [leaseOptionNote, setLeaseOptionNote] = useState("");

  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [restoring, setRestoring] = useState(false);

  // "Already set up" confirmation shown when Setup Payments is clicked but a
  // payment setup already exists for this property.
  const [alreadySetupModal, setAlreadySetupModal] = useState(false);
  const [resetupSaving, setResetupSaving] = useState(false);

  const loadProperty = async () => {
    try {
      const p = await getPropertyById(propertyId);
      if (!p) {
        navigate("/properties");
        return;
      }
      setProperty(p);
      setAddress(p.address || "");
      setAccessList(getPropertyAccessList(p));
    } catch (e) {
      console.error("Load property error:", e);
    } finally {
      setLoading(false);
    }
  };

  const loadTenants = async () => {
    setTenantLoading(true);
    try {
      setTenants(await getTenantsByProperty(propertyId));
    } catch (e) {
      console.error("Load property tenants error:", e);
    } finally {
      setTenantLoading(false);
    }
  };

  const loadActiveLease = async () => {
    setLeaseLoading(true);
    try {
      // Use createdBy query (matches "list" security rule without external reads),
      // then find the active lease for this property in memory.
      const allLeases = await getLeasesByOwner(user.uid);
      const active = allLeases.find(
        (l) => l.propertyId === propertyId && l.status === "Active" && !l.deleted
      ) || null;
      setActiveLease(active);
      if (active?.id) {
        try {
          const pmts = await getPaymentsByLease(user.uid, active.id);
          setLeasePayments(pmts);
          checkAndNotifyLateRent(active, pmts).catch((e) => console.error("Late rent check error:", e));
        } catch { /* no payments yet */ }
      } else {
        setLeasePayments([]);
      }
    } catch (e) {
      console.error("Load active lease error:", e);
    } finally {
      setLeaseLoading(false);
    }
  };

  const openAccessModal = () => {
    setAccessError("");
    setAccessEmail("");
    setAccessRole(PROPERTY_ACCESS_VIEWER);
    setAccessModal(true);
  };

  const handleShareAccess = async () => {
    const email = accessEmail.trim().toLowerCase();
    setAccessError("");
    if (!email) {
      setAccessError("Enter an email address.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setAccessError("Enter a valid email address.");
      return;
    }
    if (email === (user?.email || "").toLowerCase()) {
      setAccessError("You already have access to this property.");
      return;
    }
    setAccessSaving(true);
    try {
      await sharePropertyAccess(propertyId, email, accessRole);
      const fresh = await getPropertyById(propertyId);
      setProperty(fresh);
      setAccessList(getPropertyAccessList(fresh));
      setAccessModal(false);
    } catch (e) {
      console.error("Share property access error:", e);
      setAccessError("Failed to share access. Please try again.");
    } finally {
      setAccessSaving(false);
    }
  };

  const handleChangeAccessRole = async (email, role) => {
    setAccessSaving(true);
    try {
      await sharePropertyAccess(propertyId, email, role);
      const fresh = await getPropertyById(propertyId);
      setProperty(fresh);
      setAccessList(getPropertyAccessList(fresh));
    } catch (e) {
      console.error("Change access role error:", e);
    } finally {
      setAccessSaving(false);
    }
  };

  const handleRevokeAccess = async () => {
    if (!revokeTarget) return;
    setAccessSaving(true);
    try {
      await revokePropertyAccess(propertyId, revokeTarget.email);
      const fresh = await getPropertyById(propertyId);
      setProperty(fresh);
      setAccessList(getPropertyAccessList(fresh));
      setRevokeTarget(null);
      setRevokeModal(false);
    } catch (e) {
      console.error("Revoke access error:", e);
    } finally {
      setAccessSaving(false);
    }
  };

  /** Prefills the same modal with the active lease's fields, in "edit" mode
   * (updates the existing doc in place instead of soft-deleting + recreating
   * one) — tenant selection stays read-only here; reassigning tenants goes
   * through "Delete & Set Up Again" since that touches invitations too. */
  const openEditLeaseModal = () => {
    if (!activeLease) return;
    setLeaseError("");
    setLeaseMode("simple");
    setEditingLease(true);
    setLeaseForm({
      startDate: activeLease.startDate || "",
      endDate: activeLease.endDate || "",
      monthlyRent: activeLease.monthlyRent != null ? String(activeLease.monthlyRent) : "",
      securityDeposit: activeLease.securityDeposit != null ? String(activeLease.securityDeposit) : "",
      rentDueDay: activeLease.rentDueDay != null ? String(activeLease.rentDueDay) : "1",
      firstPaymentDate: activeLease.firstPaymentDate || "",
      notes: activeLease.notes || "",
    });
    setCreateLeaseModal(true);
  };

  const handleSaveLease = async () => {
    setLeaseError("");
    if (!leaseForm.startDate || !leaseForm.endDate) {
      setLeaseError("Start date and end date are required.");
      return;
    }
    if (!(parseFloat(leaseForm.monthlyRent) > 0)) {
      setLeaseError("Enter the monthly rent amount tenants will pay.");
      return;
    }
    if (editingLease) {
      setLeaseSaving(true);
      try {
        await updateLease(activeLease.id, {
          startDate: leaseForm.startDate,
          endDate: leaseForm.endDate,
          monthlyRent: parseFloat(leaseForm.monthlyRent) || 0,
          securityDeposit: parseFloat(leaseForm.securityDeposit) || 0,
          rentDueDay: parseInt(leaseForm.rentDueDay) || 1,
          firstPaymentDate: leaseForm.firstPaymentDate,
          notes: leaseForm.notes,
        });
        setCreateLeaseModal(false);
        setEditingLease(false);
        await loadActiveLease();
      } catch (e) {
        console.error("Update payment setup error:", e);
        setLeaseError("Failed to save changes.");
      } finally {
        setLeaseSaving(false);
      }
      return;
    }
    // Every current tenant on this property is attached to the lease
    // automatically — tenant/payments membership is managed entirely from
    // the Tenants pane (add/invite/unlink), not from this modal.
    setLeaseSaving(true);
    try {
      const tenantObjs = tenants;
      // The landlord on a lease is the verified property owner. Anyone granted
      // editor access to the property is included so they can view and sign too.
      const ownerEmail = (user.email || "").toLowerCase().trim();
      const editorEmails = accessList
        .filter((a) => a.role === PROPERTY_ACCESS_EDITOR)
        .map((a) => a.email);
      const landlordEmails = [ownerEmail, ...editorEmails].filter(Boolean);
      const leaseId = await createLease({
        propertyId,
        propertyAddress: property.address,
        createdBy: user.uid,
        landlordId: null,
        landlordIds: [],
        landlordNames: [user.displayName || ownerEmail, ...editorEmails],
        landlordEmails,
        landlordUid: user.uid,
        tenantIds: tenantObjs.map((t) => t.id),
        tenantEmails: tenantObjs.map((t) => t.email.toLowerCase().trim()),
        tenantNames: tenantObjs.map((t) => t.name || t.email),
        startDate: leaseForm.startDate,
        endDate: leaseForm.endDate,
        monthlyRent: parseFloat(leaseForm.monthlyRent) || 0,
        securityDeposit: parseFloat(leaseForm.securityDeposit) || 0,
        rentDueDay: parseInt(leaseForm.rentDueDay) || 1,
        firstPaymentDate: leaseForm.firstPaymentDate,
        notes: leaseForm.notes,
        // Payment setup only — no lease document, so nothing to sign.
        requiresSignature: false,
      });

      // Stamp the setup on each tenant record so their rental page can load it
      // with a direct document read instead of a collection query. No new
      // invitation here — addTenantByEmail already sent one when the tenant
      // was first added on the Tenants pane; re-inviting here just produced
      // a second pending-invitation card for the same person.
      await Promise.all(
        tenantObjs.map((t) => updateTenant(t.id, { leaseId }).catch(console.error))
      );

      setCreateLeaseModal(false);
      await Promise.all([loadActiveLease(), loadTenants()]);
    } catch (e) {
      console.error("Payment setup error:", e);
      setLeaseError("Failed to save the payment setup.");
    } finally {
      setLeaseSaving(false);
    }
  };

  const closeLeaseModal = () => { setCreateLeaseModal(false); setEditingLease(false); };

  const openRecordPaymentModal = () => {
    setRecordPaymentError("");
    setEditingPaymentId(null);
    const today = new Date();
    setRecordPaymentForm({
      tenantId: activeLease?.tenantIds?.[0] || "",
      type: "rent",
      month: `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`,
      amount: activeLease?.monthlyRent ? String(activeLease.monthlyRent) : "",
      paidAt: today.toISOString().slice(0, 10),
      note: "",
    });
    setRecordPaymentModal(true);
  };

  /** Prefills the same modal with an existing manual payment's fields —
   * only ever called for `source === "manual"` rows (see the table below). */
  const openEditPaymentModal = (payment) => {
    setRecordPaymentError("");
    setEditingPaymentId(payment.id);
    const tenantObj = tenants.find((t) => t.email === payment.tenantEmail);
    const paidAtDate = payment.paidAt instanceof Date
      ? payment.paidAt
      : payment.paidAt?.toDate
        ? payment.paidAt.toDate()
        : null;
    setRecordPaymentForm({
      tenantId: tenantObj?.id || "",
      type: payment.type === "deposit" ? "deposit" : "rent",
      month: payment.month || "",
      amount: String(payment.amount ?? ""),
      paidAt: paidAtDate ? paidAtDate.toISOString().slice(0, 10) : "",
      note: payment.note || "",
    });
    setRecordPaymentModal(true);
  };

  const handleRecordPayment = async () => {
    setRecordPaymentError("");
    if (!recordPaymentForm.tenantId) {
      setRecordPaymentError("Select which tenant this payment is for.");
      return;
    }
    if (recordPaymentForm.type === "rent" && !recordPaymentForm.month) {
      setRecordPaymentError("Enter the month this payment covers (e.g. 2026-05).");
      return;
    }
    if (!(parseFloat(recordPaymentForm.amount) > 0)) {
      setRecordPaymentError("Enter the amount paid.");
      return;
    }
    setRecordPaymentSaving(true);
    try {
      const tenantObj = tenants.find((t) => t.id === recordPaymentForm.tenantId);
      const payload = {
        tenantUid: tenantObj?.uid || null,
        tenantEmail: tenantObj?.email || "",
        tenantName: tenantObj?.name || tenantObj?.email || "",
        type: recordPaymentForm.type,
        month: recordPaymentForm.month,
        amount: parseFloat(recordPaymentForm.amount) || 0,
        paidAt: recordPaymentForm.paidAt,
        note: recordPaymentForm.note,
      };
      if (editingPaymentId) {
        await updateManualPayment(editingPaymentId, payload);
      } else {
        await recordManualPayment(user.uid, { leaseId: activeLease.id, propertyId, ...payload });
      }
      setRecordPaymentModal(false);
      setEditingPaymentId(null);
      await loadActiveLease();
    } catch (e) {
      console.error("Record payment error:", e);
      setRecordPaymentError(e.message || "Failed to record the payment.");
    } finally {
      setRecordPaymentSaving(false);
    }
  };

  const openDeletePaymentModal = (payment) => {
    setDeletePaymentTarget(payment);
    setDeletePaymentModal(true);
  };

  const handleDeletePayment = async () => {
    if (!deletePaymentTarget) return;
    setDeletePaymentSaving(true);
    try {
      await deleteManualPayment(deletePaymentTarget.id);
      setDeletePaymentModal(false);
      setDeletePaymentTarget(null);
      await loadActiveLease();
    } catch (e) {
      console.error("Delete payment error:", e);
    } finally {
      setDeletePaymentSaving(false);
    }
  };

  const loadApplications = async () => {
    setAppsLoading(true);
    try {
      setApplications(await getApplicationsByProperty(propertyId));
    } catch (e) {
      console.error("Load applications error:", e);
    } finally {
      setAppsLoading(false);
    }
  };

  const openApplicationsModal = async () => {
    setAppSearch("");
    setSelectedApp(null);
    setSelectedListingId(null);
    setConfirmDeleteListing("");
    setAppStatusError("");
    setListingError("");
    setAppModal(true);
    await loadListings();
  };

  const openListingApplications = (listingId) => {
    setSelectedListingId(listingId);
    setAppSearch("");
    setAppStatusError("");
    setSelectedApp(applications.find((a) => a.listingId === listingId) || null);
  };

  const handleUpdateAppStatus = async (appId, status) => {
    setAppStatusError("");
    try {
      // Only one applicant may be approved per application link. The other
      // approval has to be cleared first — we never auto-reject anyone.
      if (status === "approved") {
        const target = applications.find((a) => a.id === appId);
        const other = applications.find(
          (a) => a.id !== appId && a.listingId === target?.listingId && a.status === "approved"
        );
        if (other) {
          setAppStatusError(
            `${other.name || other.email} is already approved for this application link. Mark them as rejected or reviewing before approving someone else.`
          );
          return;
        }
      }
      await updateApplicationStatus(appId, status);
      setApplications((prev) =>
        prev.map((a) => (a.id === appId ? { ...a, status } : a))
      );
      if (selectedApp?.id === appId) setSelectedApp((a) => ({ ...a, status }));
    } catch (e) {
      console.error("Update app status error:", e);
      setAppStatusError("Failed to update the application status.");
    }
  };

  /** Add an applicant to the property — just their email, nothing else. */
  const handleAddTenantFromApp = async (app) => {
    setAppModal(false);
    setTenantError("");
    setNewTenantEmail(app.email || "");
    setAddTenantModal(true);
  };

  // ── Rental application links (listings) ───────────────────────────

  const applyLinkFor = (id) => `${window.location.origin}/rentals/${id}`;

  const loadListings = async () => {
    setListingsLoading(true);
    try {
      const propertyListings = await getListingsByProperty(propertyId);
      setListings(propertyListings);
      syncPublicListings(propertyListings).catch((e) => {
        console.warn("Public listing sync failed:", e);
      });
    } catch (e) {
      console.error("Load listings error:", e);
    } finally {
      setListingsLoading(false);
    }
  };

  const loadListingFeedback = async () => {
    if (!user?.uid) return;
    setFeedbackLoading(true);
    try {
      setListingFeedback(await getListingFeedbackByProperty(propertyId, user.uid));
    } catch (e) {
      console.error("Load listing feedback error:", e);
    } finally {
      setFeedbackLoading(false);
    }
  };

  const openListingModal = async () => {
    // Application links require a payout method on file — redirect instead
    // of opening the create form if that hasn't been set up yet.
    if (!stripeStatus?.connected || !stripeStatus?.onboardingComplete) {
      navigate("/payments");
      return;
    }
    setListingError("");
    setNewListingId("");
    setCopiedLink("");
    setEditingListingId("");
    setListingForm({ title: "", monthlyRent: "", deposit: "", bedrooms: "", bathrooms: "", sqft: property?.squareFeet ?? "", availableDate: "", description: "", petPolicy: "no-pets", leaseTerm: "month-to-month" });
    listingImages.filter((image) => image.source === "new").forEach((image) => URL.revokeObjectURL(image.previewUrl));
    setListingImages([]);
    setListingModal(true);
    await loadListings();
  };

  const openEditListingModal = async (listing) => {
    setListingError("");
    setNewListingId("");
    setCopiedLink("");
    setEditingListingId(listing.id);
    setListingForm({
      title: listing.title && listing.title !== listing.address ? listing.title : "",
      monthlyRent: listing.monthlyRent ?? "",
      deposit: listing.deposit ?? "",
      bedrooms: listing.bedrooms ?? "",
      bathrooms: listing.bathrooms ?? "",
      sqft: listing.sqft ?? property?.squareFeet ?? "",
      availableDate: listing.availableDate ?? "",
      description: listing.description ?? "",
      petPolicy: listing.petPolicy || "no-pets",
      leaseTerm: listing.leaseTerm || "month-to-month",
    });
    listingImages.filter((image) => image.source === "new").forEach((image) => URL.revokeObjectURL(image.previewUrl));
    const existingImages = (listing.images || []).map((image, index) => ({
      id: `existing-${image.key || index}`,
      source: "existing",
      image,
      previewUrl: image.url || "",
    }));
    setListingImages(existingImages);
    setListingModal(true);

    const resolvedImages = await Promise.all(existingImages.map(async (item) => ({
      ...item,
      previewUrl: item.previewUrl || (item.image.key
        ? await getListingImageUrl(item.image.key).catch(() => "")
        : ""),
    })));
    setListingImages((current) => current.map((item) =>
      resolvedImages.find((resolved) => resolved.id === item.id) || item
    ));
  };

  const handleAddListingImageFiles = (files) => {
    const addedAt = Date.now();
    const staged = Array.from(files || []).map((file, index) => ({
      id: `new-${addedAt}-${index}-${file.name}`,
      source: "new",
      file,
      previewUrl: URL.createObjectURL(file),
    }));
    setListingImages((previous) => [...previous, ...staged]);
  };

  const handleRemoveListingImage = (imageId) => {
    setListingImages((previous) => {
      const target = previous.find((image) => image.id === imageId);
      if (target?.source === "new") URL.revokeObjectURL(target.previewUrl);
      return previous.filter((image) => image.id !== imageId);
    });
  };

  const moveListingImage = (imageId, destinationIndex) => {
    setListingImages((previous) => {
      const sourceIndex = previous.findIndex((image) => image.id === imageId);
      if (sourceIndex < 0) return previous;
      const nextIndex = Math.max(0, Math.min(destinationIndex, previous.length - 1));
      if (sourceIndex === nextIndex) return previous;
      const reordered = [...previous];
      const [moved] = reordered.splice(sourceIndex, 1);
      reordered.splice(nextIndex, 0, moved);
      return reordered;
    });
  };

  const uploadOrderedListingImages = async (listingId) => {
    const uploadedEntries = await Promise.all(
      listingImages
        .filter((image) => image.source === "new")
        .map(async (image) => [image.id, await uploadListingImage(listingId, image.file)])
    );
    const uploadedById = new Map(uploadedEntries);
    return listingImages.map((image) =>
      image.source === "existing" ? image.image : uploadedById.get(image.id)
    );
  };

  const handleCreateListing = async () => {
    setListingError("");
    if (listings.some((l) => l.active !== false)) {
      setListingError("There is already an active application link. Retire it before creating a new one.");
      return;
    }
    if (!listingForm.monthlyRent) {
      setListingError("Monthly rent is required.");
      return;
    }
    setListingSaving(true);
    try {
      // One-time Places lookup — cached on the listing doc so the public
      // application page never has to call the Places API itself.
      const nearbyPlaces = await computeNearbyPlaces(property?.address).catch((e) => {
        console.error("Nearby places lookup failed:", e);
        return null;
      });
      const id = await createListing({
        propertyId,
        createdBy: user.uid,
        title: listingForm.title.trim(),
        address: property?.address || "",
        monthlyRent: Number(listingForm.monthlyRent) || 0,
        deposit: Number(listingForm.deposit) || 0,
        bedrooms: listingForm.bedrooms,
        bathrooms: listingForm.bathrooms,
        sqft: listingForm.sqft,
        availableDate: listingForm.availableDate,
        description: listingForm.description.trim(),
        petPolicy: listingForm.petPolicy,
        leaseTerm: listingForm.leaseTerm,
        nearbyPlaces,
      });
      if (listingImages.length > 0) {
        setListingImagesUploading(true);
        try {
          await updateListing(id, { images: await uploadOrderedListingImages(id) });
        } finally {
          setListingImagesUploading(false);
        }
      }
      setNewListingId(id);
      setListings(await getListingsByProperty(propertyId).catch(() => listings));
    } catch (e) {
      console.error("Create listing error:", e);
      setListingError("Failed to create the application link.");
    } finally {
      setListingSaving(false);
    }
  };

  const handleUpdateListing = async () => {
    setListingError("");
    if (!listingForm.monthlyRent) {
      setListingError("Monthly rent is required.");
      return;
    }
    setListingSaving(true);
    try {
      setListingImagesUploading(true);
      const orderedImages = await uploadOrderedListingImages(editingListingId);
      await updateListing(editingListingId, {
        title: listingForm.title.trim(),
        monthlyRent: Number(listingForm.monthlyRent) || 0,
        deposit: Number(listingForm.deposit) || 0,
        bedrooms: listingForm.bedrooms,
        bathrooms: listingForm.bathrooms,
        sqft: listingForm.sqft,
        availableDate: listingForm.availableDate,
        description: listingForm.description.trim(),
        petPolicy: listingForm.petPolicy,
        leaseTerm: listingForm.leaseTerm,
        images: orderedImages,
      });
      setListings(await getListingsByProperty(propertyId).catch(() => listings));
      setListingModal(false);
    } catch (e) {
      console.error("Update listing error:", e);
      setListingError("Failed to update the application link.");
    } finally {
      setListingImagesUploading(false);
      setListingSaving(false);
    }
  };

  /** Applications submitted through a given link. */
  const appsForListing = (listingId) => applications.filter((a) => a.listingId === listingId);

  /** Retire an active link, or re-open a retired one when nothing else is active. */
  const handleToggleListingActive = async (listing) => {
    setListingError("");
    const nextActive = listing.active === false;
    if (nextActive && listings.some((l) => l.id !== listing.id && l.active !== false)) {
      setListingError("Another application link is already active. Retire it first.");
      return;
    }
    setListingBusy(listing.id);
    try {
      await updateListing(listing.id, { active: nextActive });
      setListings((prev) => prev.map((l) => (l.id === listing.id ? { ...l, active: nextActive } : l)));
    } catch (e) {
      console.error("Toggle listing error:", e);
      setListingError("Failed to update the application link.");
    } finally {
      setListingBusy("");
    }
  };

  const handleDeleteListing = async (listing) => {
    setListingError("");
    if (appsForListing(listing.id).length > 0) {
      setListingError("This link already has applications and can't be deleted. Retire it instead.");
      return;
    }
    setListingBusy(listing.id);
    try {
      await deleteListing(listing.id);
      setListings((prev) => prev.filter((l) => l.id !== listing.id));
      setConfirmDeleteListing("");
      if (selectedListingId === listing.id) setSelectedListingId(null);
    } catch (e) {
      console.error("Delete listing error:", e);
      setListingError("Failed to delete the application link.");
    } finally {
      setListingBusy("");
    }
  };

  const handleCopyLink = async (link) => {
    try {
      await navigator.clipboard.writeText(link);
      setCopiedLink(link);
      setTimeout(() => setCopiedLink(""), 2000);
    } catch (e) {
      console.error("Clipboard copy failed:", e);
    }
  };

  const scrollToId = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

  const filteredApps = applications.filter((a) => {
    if (selectedListingId && a.listingId !== selectedListingId) return false;
    if (!appSearch.trim()) return true;
    const q = appSearch.toLowerCase();
    return (
      a.name?.toLowerCase().includes(q) ||
      a.email?.toLowerCase().includes(q) ||
      a.status?.toLowerCase().includes(q)
    );
  });

  useEffect(() => {
    loadProperty();
    loadTenants();
    loadActiveLease();
    loadApplications();
    loadListings();
    loadListingFeedback();
    getStripeAccountStatus().then(setStripeStatus).catch((e) => console.error("Load Stripe status error:", e));
  }, [propertyId]);

  // Load Google Maps script once (reused if RentalApplicationPage or another
  // page already loaded it) — needed for the one-time nearby-places lookup.
  useEffect(() => {
    const key = import.meta.env.VITE_APP_GOOGLE_MAPS_API_KEY;
    if (!key || document.querySelector("[data-gmaps]")) return;
    const s = document.createElement("script");
    s.src = `https://maps.googleapis.com/maps/api/js?key=${key}&libraries=places`;
    s.async = true; s.defer = true; s.dataset.gmaps = "1";
    document.head.appendChild(s);
  }, []);

  // Keep the property's chat roster in step with its tenants. Also backfills
  // properties created before tenantEmails existed.
  useEffect(() => {
    if (!property || tenantLoading || property.createdBy !== user?.uid) return;
    const expected = [...new Set(
      tenants
        .filter((t) => t.inviteStatus !== "declined")
        .map((t) => (t.email || "").toLowerCase())
        .filter(Boolean)
    )].sort();
    const current = [...(property.tenantEmails || [])].sort();
    if (expected.join("|") === current.join("|")) return;
    updateProperty(propertyId, { tenantEmails: expected }).catch(console.error);
  }, [property, tenants, tenantLoading, user?.uid, propertyId]);

  useEffect(() => {
    if (!user || !propertyId) return;
    (async () => {
      setTicketsLoading(true);
      try {
        const all = await getTicketsByPmUid(user.uid);
        setTickets(all.filter((t) => t.propertyId === propertyId));
      } catch (e) {
        console.error("Load property tickets error:", e, { myUid: user.uid, propertyId });
      } finally {
        setTicketsLoading(false);
      }
    })();
  }, [user, propertyId]);

  const handleSave = async () => {
    if (!address.trim()) return;
    setSaving(true);
    setSaveSuccess(false);
    try {
      await updateProperty(propertyId, { address: address.trim() });
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (e) {
      console.error("Save property error:", e);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleteError("");
    setDeleting(true);
    try {
      await deleteProperty(propertyId);
      await loadProperty();
      setDeleteModal(false);
    } catch (e) {
      console.error("Delete property error:", e);
      setDeleteError("Failed to delete. Please try again.");
    } finally {
      setDeleting(false);
    }
  };

  const handleSetupPaymentsClick = () => {
    if (activeLease) {
      setAlreadySetupModal(true);
      return;
    }
    setLeaseError("");
    setLeaseMode("simple");
    setEditingLease(false);
    setLeaseForm({ startDate: "", endDate: "", monthlyRent: "", securityDeposit: "", rentDueDay: "1", firstPaymentDate: "", notes: "" });
    setCreateLeaseModal(true);
  };

  const handleResetupPayments = async () => {
    if (!activeLease) return;
    setResetupSaving(true);
    try {
      await softDeleteLease(activeLease.id);
      setAlreadySetupModal(false);
      await loadActiveLease();
      setLeaseError("");
      setLeaseMode("simple");
      setEditingLease(false);
      setLeaseForm({ startDate: "", endDate: "", monthlyRent: "", securityDeposit: "", rentDueDay: "1", firstPaymentDate: "", notes: "" });
      setCreateLeaseModal(true);
    } catch (e) {
      console.error("Delete payment setup error:", e);
    } finally {
      setResetupSaving(false);
    }
  };

  const handleRestore = async () => {
    setRestoring(true);
    try {
      await restoreProperty(propertyId);
      await loadProperty();
    } catch (e) {
      console.error("Restore property error:", e);
    } finally {
      setRestoring(false);
    }
  };

  const openAddTenantModal = () => {
    setTenantError("");
    setNewTenantEmail("");
    setAddTenantModal(true);
  };

  /** Add someone to this property by email and send them an invitation. If
   * payments are already set up, also attaches them to the lease (arrays,
   * not just the tenant doc) so they can pay rent without a separate step. */
  const handleAddTenantEmail = async (rawEmail) => {
    const email = (rawEmail ?? newTenantEmail).trim().toLowerCase();
    setTenantError("");
    if (!email) {
      setTenantError("Please enter an email address.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setTenantError("Please enter a valid email address.");
      return;
    }
    setTenantSaving(true);
    try {
      const targetId = await addTenantByEmail(propertyId, email, {
        createdByUid: user.uid,
        leaseId: activeLease?.id || null,
        existingTenants: tenants,
      });
      if (activeLease?.id) {
        await addTenantToLease(activeLease.id, { tenantId: targetId, email }).catch(console.error);
      }
      setAddTenantModal(false);
      setNewTenantEmail("");
      await Promise.all([loadTenants(), loadActiveLease()]);
    } catch (e) {
      console.error("Add tenant error:", e);
      setTenantError(e.message || "Failed to add tenant.");
    } finally {
      setTenantSaving(false);
    }
  };

  const handleRemoveTenantFromProperty = async () => {
    if (!removeTenantTarget) return;
    setTenantSaving(true);
    try {
      await updateTenant(removeTenantTarget.id, { propertyId: "" });
      await removePropertyTenantEmail(propertyId, removeTenantTarget.email).catch(console.error);
      setRemoveTenantTarget(null);
      setRemoveTenantModal(false);
      await loadTenants();
    } catch (e) {
      console.error("Unlink tenant error:", e);
    } finally {
      setTenantSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="page-content text-center py-5">
        <Spinner color="primary" />
      </div>
    );
  }

  if (!property) return null;

  // Property approval was removed — kept as a constant so the (now always-on)
  // gating below doesn't need touching at every call site.
  const isApproved = true;
  const notApprovedTitle = "";
  // Declined invites are hidden so the owner can simply re-add the email.
  const visibleTenants = tenants.filter((t) => t.inviteStatus !== "declined");
  // Who a manual payment can be attributed to — scoped to the lease's own
  // tenants (not every tenant on the property) since that's who "the tenant
  // pane" actually assigned to pay rent here.
  const leaseTenants = activeLease
    ? visibleTenants.filter((t) => (activeLease.tenantIds || []).includes(t.id))
    : [];
  const depositPaid = leasePayments.some(
    (p) => (Array.isArray(p.items) && p.items.some((it) => it.type === "deposit")) || p.type === "deposit"
  );
  const averageFeedbackRating = listingFeedback.length
    ? listingFeedback.reduce((sum, item) => sum + (Number(item.rating) || 0), 0) / listingFeedback.length
    : 0;

  return (
    <div className="page-content">
      <Container fluid>
        {/* Hero banner */}
        <div
          className="rounded mb-4 p-4 text-white position-relative overflow-hidden"
          style={{ minHeight: 140 }}
        >
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: "linear-gradient(135deg, #556ee6 0%, #34c38f 100%)",
            }}
          />
          <div className="position-relative" style={{ zIndex: 1 }}>
            <Row className="align-items-center">
              <Col>
                <p className="mb-1 opacity-75">
                  <Link to="/properties" className="text-white text-decoration-underline">
                    All Properties
                  </Link>
                </p>
                <h3 className="mb-0 text-white text-truncate">{property.address || "Property"}</h3>
              </Col>
              <Col xs="auto">
                <Button color="light" outline onClick={() => navigate("/properties")}>
                  <i className="bx bx-arrow-back me-1" /> Back
                </Button>
              </Col>
            </Row>
          </div>
        </div>

        {/* Deleted banner */}
        {property.deleted && (
          <Alert color="danger" className="mb-4 d-flex align-items-center justify-content-between">
            <span>
              <i className="bx bx-trash me-2" />
              <strong>This property has been deleted.</strong>{" "}
              It is no longer visible in the properties list.
            </span>
            <Button color="danger" size="sm" disabled={restoring} onClick={handleRestore}>
              {restoring ? <Spinner size="sm" /> : <><i className="bx bx-undo me-1" />Restore</>}
            </Button>
          </Alert>
        )}

        {/* Setup checklist alert */}
        {!tenantLoading && !leaseLoading && (() => {
          const missing = [];
          if (visibleTenants.length === 0)  missing.push({ icon: "bx bxs-user",         label: "No tenant assigned", onClick: () => scrollToId("tenants-section") });
          if (!activeLease)          missing.push({ icon: "bx bxs-file-blank",   label: "No payment setup", onClick: () => scrollToId("lease-section") });
          if (missing.length === 0) return null;
          return (
            <Alert color="warning" className="mb-4 d-flex align-items-start gap-3">
              <i className="bx bxs-error-circle fs-4 flex-shrink-0 mt-1" />
              <div>
                <strong>This property is missing the following:</strong>
                <ul className="mb-0 mt-1 ps-3">
                  {missing.map(({ icon, label, onClick }) => (
                    <li key={label}>
                      <i className={`${icon} me-1`} />
                      {onClick ? (
                        <button
                          className="btn btn-link btn-sm p-0 align-baseline text-warning fw-semibold"
                          style={{ textDecoration: "underline dotted" }}
                          onClick={onClick}
                        >{label}</button>
                      ) : label}
                    </li>
                  ))}
                </ul>
              </div>
            </Alert>
          );
        })()}

        {/* ── Rental actions ── */}
        <Row className="g-4">
          <Col md={12}>
            <Card>
              <CardBody>
                <h5 className="mb-3">Rental</h5>

                <Row className="g-3">
                  <Col md={4}>
                    <Card className="h-100 border">
                      <CardBody className="p-3">
                        <div className="d-flex align-items-center mb-2">
                          <div className="rounded-circle d-inline-flex align-items-center justify-content-center me-2" style={{ width: 36, height: 36, background: "#556ee618" }}>
                            <i className="bx bx-user-check" style={{ color: "#556ee6" }} />
                          </div>
                          <h6 className="mb-0">Rental Application</h6>
                        </div>
                        <p className="text-muted small mb-3">Create a shareable application link and review submitted applications.</p>
                        <Button
                          size="sm"
                          color="primary"
                          onClick={openApplicationsModal}
                          disabled={appsLoading}
                        >
                          {appsLoading ? <Spinner size="sm" className="me-1" /> : null}
                          Manage Applications
                          {!appsLoading && (
                            <Badge color="light" pill className="ms-1 text-dark">{applications.length}</Badge>
                          )}
                        </Button>
                      </CardBody>
                    </Card>
                  </Col>
                  <Col md={4}>
                    <Card className="h-100 border">
                      <CardBody className="p-3">
                        <div className="d-flex align-items-center mb-2">
                          <div className="rounded-circle d-inline-flex align-items-center justify-content-center me-2" style={{ width: 36, height: 36, background: "#f1b44c18" }}>
                            <i className="bx bx-file" style={{ color: "#f1b44c" }} />
                          </div>
                          <h6 className="mb-0">Create Lease</h6>
                        </div>
                        <p className="text-muted small mb-3">Digital lease creation and signing is coming soon. Rent collection does not require a lease.</p>
                        <Button
                          size="sm"
                          color="warning"
                          onClick={() => { setLeaseOptionNote(""); setLeaseOptionsModal(true); }}
                          disabled={!isApproved}
                          title={!isApproved ? notApprovedTitle : ""}
                        >
                          Create Lease
                        </Button>
                      </CardBody>
                    </Card>
                  </Col>
                  <Col md={4}>
                    <Card className="h-100 border">
                      <CardBody className="p-3">
                        <div className="d-flex align-items-center mb-2">
                          <div className="rounded-circle d-inline-flex align-items-center justify-content-center me-2" style={{ width: 36, height: 36, background: "#34c38f18" }}>
                            <i className="bx bx-credit-card" style={{ color: "#34c38f" }} />
                          </div>
                          <h6 className="mb-0">Accept Payments</h6>
                        </div>
                        <p className="text-muted small mb-3">Set up rent collection by selecting tenants. Configure payment amounts and schedules.</p>
                        <Button
                          size="sm"
                          color="success"
                          onClick={handleSetupPaymentsClick}
                          disabled={!isApproved}
                          title={!isApproved ? notApprovedTitle : ""}
                        >
                          Setup Payments
                        </Button>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Listing feedback */}
        <Row className="mt-4" id="listing-feedback-section">
          <Col md={12}>
            <Card>
              <CardBody>
                <div className="d-flex align-items-start justify-content-between gap-3 flex-wrap mb-3">
                  <div>
                    <h5 className="mb-1">
                      <i className="bx bx-star text-warning me-2" />
                      Listing Feedback
                    </h5>
                    <small className="text-muted">
                      Anonymous renter feedback from your public listing pages.
                    </small>
                  </div>
                  <Badge color={listingFeedback.length ? "warning" : "light"} className={listingFeedback.length ? "" : "text-dark border"} pill>
                    {listingFeedback.length} response{listingFeedback.length === 1 ? "" : "s"}
                  </Badge>
                </div>

                {feedbackLoading ? (
                  <div className="text-center py-3"><Spinner size="sm" color="primary" /></div>
                ) : listingFeedback.length === 0 ? (
                  <div className="text-center py-4">
                    <i className="bx bx-message-square-edit text-muted" style={{ fontSize: "2rem" }} />
                    <p className="text-muted small mb-0 mt-2">No listing feedback yet.</p>
                  </div>
                ) : (
                  <>
                    <div className="d-flex align-items-center gap-3 p-3 rounded bg-light mb-3 flex-wrap">
                      <div>
                        <small className="text-muted d-block">Average rating</small>
                        <div className="d-flex align-items-center gap-2">
                          <strong>{averageFeedbackRating.toFixed(1)}</strong>
                          <FeedbackStars rating={Math.round(averageFeedbackRating)} />
                        </div>
                      </div>
                      <div className="vr d-none d-sm-block" />
                      <small className="text-muted">
                        Feedback points to listing clarity, photos, fees, pets, tours, application steps, and neighborhood details.
                      </small>
                    </div>

                    <div className="d-flex flex-column gap-3">
                      {listingFeedback.slice(0, 8).map((item) => {
                        const listing = listings.find((l) => l.id === item.listingId);
                        const feedbackTopics = (item.topics || []).filter((topic) => typeof topic === "string");
                        return (
                          <div key={item.id} className="border rounded p-3">
                            <div className="d-flex align-items-start justify-content-between gap-3 flex-wrap">
                              <div>
                                <div className="fw-semibold">
                                  {listing ? getListingDisplayTitle(listing) : "Rental listing"}
                                </div>
                                <div className="d-flex align-items-center gap-2 mt-1">
                                  <FeedbackStars rating={item.rating} />
                                  <small className="text-muted">
                                    {item.createdAt ? item.createdAt.toLocaleDateString() : "New"}
                                  </small>
                                </div>
                              </div>
                              <Badge color="light" className="text-dark border">{item.rating}/5</Badge>
                            </div>

                            {feedbackTopics.length > 0 && (
                              <div className="d-flex flex-wrap gap-1 mt-2">
                                {feedbackTopics.map((topic) => (
                                  <Badge key={topic} color="light" className="text-dark border">
                                    {FEEDBACK_TOPIC_LABELS[topic] || topic}
                                  </Badge>
                                ))}
                              </div>
                            )}

                            {item.message && (
                              <p className="text-muted small mb-0 mt-2" style={{ whiteSpace: "pre-wrap" }}>
                                {item.message}
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Payments Section */}
        <Row className="mt-4" id="lease-section">
          <Col md={12}>
            <Card>
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0">
                    <i className="bx bx-credit-card text-success me-2" />
                    Payments
                  </h5>
                  {!leaseLoading && !activeLease && isApproved && (
                    <Button
                      color="success"
                      size="sm"
                      onClick={handleSetupPaymentsClick}
                    >
                      <i className="bx bx-plus me-1" /> Setup Payments
                    </Button>
                  )}
                  {!leaseLoading && activeLease && (
                    <Button color="link" size="sm" className="p-0" onClick={openEditLeaseModal}>
                      <i className="bx bx-edit-alt me-1" />Edit
                    </Button>
                  )}
                </div>
                {leaseLoading ? (
                  <div className="text-center py-3"><Spinner size="sm" color="primary" /></div>
                ) : activeLease ? (
                  <div>
                    <div className="bg-light p-3 rounded mb-2">
                      <Row>
                        <Col sm={4}>
                          <small className="text-muted d-block">Status</small>
                          <Badge color="success">{activeLease.status}</Badge>
                        </Col>
                        <Col sm={4}>
                          <small className="text-muted d-block">Start</small>
                          <strong>{activeLease.startDate}</strong>
                        </Col>
                        <Col sm={4}>
                          <small className="text-muted d-block">End</small>
                          <strong>{activeLease.endDate}</strong>
                        </Col>
                      </Row>
                      <Row className="mt-2">
                        <Col sm={4}>
                          <small className="text-muted d-block">Monthly Rent</small>
                          <strong>${(activeLease.monthlyRent || 0).toLocaleString()}</strong>
                        </Col>
                        <Col sm={4}>
                          <small className="text-muted d-block">Tenants</small>
                          <strong>{(activeLease.tenantNames || []).join(", ")}</strong>
                        </Col>
                        <Col sm={4}>
                          <small className="text-muted d-block">Rent Due</small>
                          <strong>Day {activeLease.rentDueDay || 1} of each month</strong>
                        </Col>
                      </Row>
                      {activeLease.securityDeposit > 0 && (
                        <Row className="mt-2">
                          <Col sm={4}>
                            <small className="text-muted d-block">Security Deposit</small>
                            <strong>
                              ${activeLease.securityDeposit.toLocaleString()}{" "}
                              <Badge color={depositPaid ? "success" : "warning"} pill>
                                {depositPaid ? "Paid" : "Unpaid"}
                              </Badge>
                            </strong>
                          </Col>
                        </Row>
                      )}
                    </div>
                    <p className="text-muted small mb-0">
                      <i className="bx bx-info-circle me-1" />
                      Selected tenants can now pay rent from their rental page. No signature is required.
                    </p>
                    <div className="mt-3">
                      <div className="d-flex justify-content-between align-items-center">
                        <small className="text-muted text-uppercase fw-semibold" style={{ fontSize: "0.68rem", letterSpacing: "0.05em" }}>Payment History</small>
                        <Button color="link" size="sm" className="p-0" onClick={openRecordPaymentModal}>
                          <i className="bx bx-plus me-1" />Record Payment
                        </Button>
                      </div>
                      {leasePayments.length === 0 ? (
                        <p className="text-muted small fst-italic mb-0 mt-1">No payments recorded yet.</p>
                      ) : (
                        <div className="table-responsive mt-1">
                          <Table size="sm" className="mb-0">
                            <thead className="table-light">
                              <tr>
                                <th>Month</th>
                                <th>Tenant</th>
                                <th>Amount</th>
                                <th>Source</th>
                                <th>Paid At</th>
                                <th></th>
                              </tr>
                            </thead>
                            <tbody>
                              {leasePayments.map((p) => (
                                <tr key={p.id}>
                                  <td>{paymentSummaryLabel(p)}</td>
                                  <td className="text-muted small">{p.tenantEmail}</td>
                                  <td>${(p.amount || 0).toLocaleString()}</td>
                                  <td className="text-muted small">{p.last4 ? `•••• ${p.last4}` : p.source === "manual" ? "Manual" : "—"}</td>
                                  <td className="text-muted small">
                                    {p.paidAt instanceof Date
                                      ? p.paidAt.toLocaleDateString()
                                      : p.paidAt?.toDate
                                        ? p.paidAt.toDate().toLocaleDateString()
                                        : "—"}
                                  </td>
                                  <td className="text-end">
                                    {p.source === "manual" && (
                                      <>
                                        <Button color="link" size="sm" className="p-0 me-2" onClick={() => openEditPaymentModal(p)}>
                                          <i className="bx bx-edit-alt" />
                                        </Button>
                                        <Button color="link" size="sm" className="p-0 text-danger" onClick={() => openDeletePaymentModal(p)}>
                                          <i className="bx bx-trash" />
                                        </Button>
                                      </>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <p className="text-muted small fst-italic mb-0">
                    No payment setup yet. Click "Setup Payments" above to configure rent collection.
                  </p>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Manage Access + Tenants */}
        <Row className="mt-4 g-4" id="access-section">
          <Col md={8}>
            <Card>
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div>
                    <h5 className="mb-0">
                      <i className="bx bx-user-plus text-warning me-2" />
                      Manage Access
                    </h5>
                    <small className="text-muted">
                      People you've shared this property with.
                    </small>
                  </div>
                  <Button color="warning" size="sm" onClick={openAccessModal}>
                    <i className="bx bx-plus me-1" /> Share
                  </Button>
                </div>

                <div className="d-flex align-items-center gap-2 p-2 mb-2 rounded bg-light">
                  <i className="bx bxs-user-circle text-warning fs-4" />
                  <div className="flex-grow-1 min-w-0">
                    <div className="fw-semibold text-truncate">{user?.email}</div>
                    <small className="text-muted">You</small>
                  </div>
                  <Badge color="warning" pill>Owner</Badge>
                </div>

                {accessList.length === 0 ? (
                  <div className="text-center py-3">
                    <p className="text-muted small mb-0">
                      Not shared with anyone else yet. Invite someone by email and they'll
                      see this property when they sign in.
                    </p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <Table className="table-hover mb-0 align-middle" size="sm">
                      <thead className="table-light">
                        <tr>
                          <th>Email</th>
                          <th style={{ width: 130 }}>Access</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {accessList.map((a) => (
                          <tr key={a.email}>
                            <td className="text-truncate" style={{ maxWidth: 200 }}>
                              <i className="bx bx-envelope me-1 text-muted" />
                              {a.email}
                            </td>
                            <td>
                              <Input
                                type="select"
                                bsSize="sm"
                                value={a.role}
                                disabled={accessSaving}
                                onChange={(e) => handleChangeAccessRole(a.email, e.target.value)}
                              >
                                <option value={PROPERTY_ACCESS_VIEWER}>Viewer</option>
                                <option value={PROPERTY_ACCESS_EDITOR}>Editor</option>
                              </Input>
                            </td>
                            <td className="text-end">
                              <Button
                                color="danger"
                                outline
                                size="sm"
                                onClick={() => {
                                  setRevokeTarget(a);
                                  setRevokeModal(true);
                                }}
                              >
                                Remove
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </CardBody>
            </Card>

            {/* Inspections */}
            <div className="mt-4" id="inspections-section">
              <InspectionsPanel
                propertyId={propertyId}
                propertyAddress={property.address}
                participantEmails={[
                  user?.email,
                  ...accessList.map((a) => a.email),
                  ...tenants.map((t) => t.email),
                ].filter(Boolean)}
                user={user}
                canManage={property.createdBy === user?.uid}
              />
            </div>

            {/* Tickets */}
            <Card className="mt-4">
              <CardBody>
                <h5 className="mb-3">
                  Tickets at this Property
                  {!ticketsLoading && (
                    <Badge color="primary" pill className="ms-2">{tickets.length}</Badge>
                  )}
                </h5>
                {ticketsLoading ? (
                  <div className="text-center py-3"><Spinner size="sm" color="primary" /></div>
                ) : tickets.length === 0 ? (
                  <p className="text-muted small fst-italic">No tickets linked to this property.</p>
                ) : (
                  <div className="table-responsive">
                    <Table className="table-hover mb-0" size="sm">
                      <thead className="table-light">
                        <tr>
                          <th>Title</th>
                          <th>Tenant</th>
                          <th>Severity</th>
                          <th>Status</th>
                          <th>Created</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tickets.map((t) => (
                          <tr key={t.id}>
                            <td>
                              <Link to={`/tickets/${t.id}`}>{t.title}</Link>
                            </td>
                            <td className="text-muted small">{t.tenantName || "—"}</td>
                            <td><Badge color={t.severity === "Urgent" ? "danger" : t.severity === "High" ? "warning" : t.severity === "Medium" ? "warning" : "info"}>{t.severity}</Badge></td>
                            <td><Badge color={t.status === "Open" ? "primary" : t.status === "In Progress" ? "warning" : "success"}>{t.status}</Badge></td>
                            <td className="text-muted small">{t.createdAt ? t.createdAt.toLocaleDateString() : "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>

          {/* Tenants */}
          <Col md={4} id="tenants-section">
            <Card>
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div>
                    <h5 className="mb-0">Tenants</h5>
                    <small className="text-muted">Assigned to this property.</small>
                  </div>
                  {tenantTab === "tenants" && (
                    <Button color="primary" size="sm" onClick={openAddTenantModal}
                      disabled={!isApproved}
                      title={!isApproved ? notApprovedTitle : ""}
                    >
                      <i className="bx bx-plus me-1" /> Add
                    </Button>
                  )}
                </div>

                <Nav tabs className="mb-3">
                  <NavItem>
                    <NavLink
                      className={tenantTab === "tenants" ? "active" : ""}
                      style={{ cursor: "pointer" }}
                      onClick={() => setTenantTab("tenants")}
                    >
                      <i className="bx bx-user me-1" />Tenants
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={tenantTab === "chat" ? "active" : ""}
                      style={{ cursor: "pointer" }}
                      onClick={() => setTenantTab("chat")}
                    >
                      <i className="bx bx-conversation me-1" />Chat
                    </NavLink>
                  </NavItem>
                </Nav>

                <TabContent activeTab={tenantTab}>
                  <TabPane tabId="tenants">
                {tenantLoading ? (
                  <div className="text-center py-3"><Spinner size="sm" color="primary" /></div>
                ) : visibleTenants.length === 0 ? (
                  <div className="text-center py-4">
                    <i className="bx bx-user text-muted" style={{ fontSize: "2rem" }} />
                    <p className="text-muted mt-2 mb-0 small">No tenants yet.{" "}
                      <button className="btn btn-link btn-sm p-0" onClick={() => isApproved && openAddTenantModal()}>Add a tenant</button>
                      {" "}or review applications from the property actions above
                      .
                    </p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <Table className="table-hover mb-0" size="sm">
                      <thead className="table-light">
                        <tr><th>Tenant</th><th></th></tr>
                      </thead>
                      <tbody>
                        {visibleTenants.map((t) => (
                          <tr key={t.id}>
                            <td>
                              <div className="d-flex align-items-center gap-1">
                                <Link to={`/tenants/${property?.portalId}/${t.id}`}>
                                  {t.name || t.email}
                                </Link>
                                {t.inviteStatus === "accepted" ? (
                                  <i
                                    className="bx bxs-check-circle text-success"
                                    title="Invitation accepted"
                                  />
                                ) : (
                                  <Badge color="warning" pill className="fw-normal" style={{ fontSize: "0.62rem" }}>
                                    Pending
                                  </Badge>
                                )}
                              </div>
                              {t.name ? (
                                <div className="text-muted" style={{ fontSize: "0.72rem" }}>{t.email}</div>
                              ) : null}
                            </td>
                            <td className="text-end">
                              <Button color="danger" outline size="sm" onClick={() => { setRemoveTenantTarget(t); setRemoveTenantModal(true); }}>
                                Unlink
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
                  </TabPane>

                  <TabPane tabId="chat">
                    {tenantTab === "chat" && (
                      <PropertyChat
                        propertyId={propertyId}
                        user={user}
                        height={460}
                        placeholder="Message tenants and everyone with access…"
                      />
                    )}
                  </TabPane>
                </TabContent>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Danger zone */}
        {!property.deleted && (
          <div className="mt-4 mb-2 d-flex justify-content-end">
            <Button
              color="danger"
              outline
              size="sm"
              onClick={() => { setDeleteError(""); setDeleteModal(true); }}
            >
              <i className="bx bx-trash me-1" /> Delete Property
            </Button>
          </div>
        )}

        {/* Add Tenant Modal — email only */}
        <Modal isOpen={addTenantModal} toggle={() => setAddTenantModal(false)}>
          <ModalHeader toggle={() => setAddTenantModal(false)}>
            Add Tenant
          </ModalHeader>
          <ModalBody>
            {tenantError && <Alert color="danger">{tenantError}</Alert>}

            <p className="text-muted small">
              Enter the tenant&apos;s email address. They&apos;ll get an invitation to
              accept the next time they sign in, and the property will appear under
              their <strong>My Rentals</strong>.
            </p>

            <FormGroup>
              <Label>Email Address</Label>
              <Input
                type="email"
                value={newTenantEmail}
                onChange={(e) => setNewTenantEmail(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") handleAddTenantEmail(); }}
                placeholder="tenant@example.com"
                autoFocus
              />
            </FormGroup>

            {applications.filter((a) => a.status === "approved").length > 0 && (
              <div className="border-top pt-3">
                <small className="text-muted fw-semibold d-block mb-2">
                  Or pick from an approved application
                </small>
                <div className="d-flex flex-wrap gap-1">
                  {applications
                    .filter((a) => a.status === "approved" && a.email)
                    .map((a) => (
                      <Button
                        key={a.id}
                        size="sm"
                        color="success"
                        outline
                        onClick={() => setNewTenantEmail(a.email)}
                      >
                        <i className="bx bx-user-check me-1" />
                        {a.email}
                      </Button>
                    ))}
                </div>
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setAddTenantModal(false)}>
              Cancel
            </Button>
            <Button
              color="primary"
              disabled={!newTenantEmail.trim() || tenantSaving}
              onClick={() => handleAddTenantEmail()}
            >
              {tenantSaving ? <Spinner size="sm" /> : "Send Invitation"}
            </Button>
          </ModalFooter>
        </Modal>

        {/* Create Lease Options Modal */}
        <Modal isOpen={leaseOptionsModal} toggle={() => setLeaseOptionsModal(false)}>
          <ModalHeader toggle={() => setLeaseOptionsModal(false)}>Create Lease</ModalHeader>
          <ModalBody>
            {leaseOptionNote && (
              <Alert color="info" className="py-2 small">{leaseOptionNote}</Alert>
            )}
            <p className="text-muted small">Choose how you'd like to add a lease for this property.</p>
            <div className="d-flex flex-column gap-2">
              <button
                type="button"
                className="btn btn-light border text-start p-3"
                onClick={() => setLeaseOptionNote("Lease uploads are coming soon. For now, rent collection works without a lease.")}
              >
                <div className="d-flex align-items-center gap-2">
                  <i className="bx bx-upload fs-4 text-warning" />
                  <div>
                    <div className="fw-semibold">Upload Lease</div>
                    <small className="text-muted">Attach a lease document you already have.</small>
                  </div>
                </div>
              </button>
              <button type="button" className="btn btn-light border text-start p-3" disabled>
                <div className="d-flex align-items-center gap-2">
                  <i className="bx bx-bot fs-4 text-primary" />
                  <div>
                    <div className="fw-semibold">
                      Create Lease with AI
                      <Badge color="secondary" pill className="ms-2 fw-normal">Coming Soon</Badge>
                    </div>
                    <small className="text-muted">Generate a lease from your property and rent details.</small>
                  </div>
                </div>
              </button>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setLeaseOptionsModal(false)}>Close</Button>
          </ModalFooter>
        </Modal>

        {/* Accept Payments Modal */}
        <Modal isOpen={createLeaseModal} toggle={closeLeaseModal} size="lg">
          <ModalHeader toggle={closeLeaseModal}>{editingLease ? "Edit Payment Setup" : "Accept Payments Setup"}</ModalHeader>
          <ModalBody>
            {leaseError && <Alert color="danger">{leaseError}</Alert>}

            {leaseMode === "simple" ? (
              <>
                <p className="text-muted small mb-3">
                  {editingLease
                    ? "Update the payment details below — tenants already paying will see the new terms immediately."
                    : "Configure payment details to enable rent collection through the tenant portal. No lease or signature is required — every tenant on this property can start paying as soon as you save."}
                </p>

                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <Label>Payment Start Date <span className="text-danger">*</span></Label>
                      <Input
                        type="date"
                        value={leaseForm.startDate}
                        onChange={(e) => setLeaseForm({ ...leaseForm, startDate: e.target.value })}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label>Payment End Date <span className="text-danger">*</span></Label>
                      <Input
                        type="date"
                        value={leaseForm.endDate}
                        onChange={(e) => setLeaseForm({ ...leaseForm, endDate: e.target.value })}
                      />
                    </FormGroup>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <Label>Monthly Rent Amount</Label>
                      <Input
                        type="number"
                        placeholder="e.g. 2000"
                        value={leaseForm.monthlyRent}
                        onChange={(e) => setLeaseForm({ ...leaseForm, monthlyRent: e.target.value })}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label>Security Deposit</Label>
                      <Input
                        type="number"
                        placeholder="e.g. 4000"
                        value={leaseForm.securityDeposit}
                        onChange={(e) => setLeaseForm({ ...leaseForm, securityDeposit: e.target.value })}
                      />
                    </FormGroup>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <Label>Rent Due Day of Month</Label>
                      <Input
                        type="number"
                        min="1"
                        max="31"
                        value={leaseForm.rentDueDay}
                        onChange={(e) => setLeaseForm({ ...leaseForm, rentDueDay: e.target.value })}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label>First Payment Date</Label>
                      <Input
                        type="date"
                        value={leaseForm.firstPaymentDate}
                        onChange={(e) => setLeaseForm({ ...leaseForm, firstPaymentDate: e.target.value })}
                      />
                    </FormGroup>
                  </Col>
                </Row>

                <FormGroup>
                  <Label>Landlord <span className="text-muted fw-normal small">(who will receive payments)</span></Label>
                  <div className="border rounded p-2">
                    <div className="d-flex align-items-center gap-2">
                      <i className="bx bxs-user-circle text-warning fs-5" />
                      <span>{user?.displayName || user?.email}</span>
                      <Badge color="info" className="small">You</Badge>
                    </div>
                    {accessList
                      .filter((a) => a.role === PROPERTY_ACCESS_EDITOR)
                      .map((a) => (
                        <div key={a.email} className="d-flex align-items-center gap-2 mt-1">
                          <i className="bx bx-user text-muted fs-5" />
                          <span className="text-muted small">{a.email}</span>
                          <Badge color="light" className="text-dark small">Editor</Badge>
                        </div>
                      ))}
                  </div>
                  <small className="text-muted">
                    Payments are collected on your behalf. Anyone with editor access is included too.
                  </small>
                </FormGroup>

                <FormGroup>
                  <Label>Notes <span className="text-muted fw-normal small">(optional)</span></Label>
                  <Input
                    type="textarea"
                    rows={3}
                    placeholder="Any additional payment terms or notes..."
                    value={leaseForm.notes}
                    onChange={(e) => setLeaseForm({ ...leaseForm, notes: e.target.value })}
                  />
                </FormGroup>
              </>
            ) : null}
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={closeLeaseModal}>
              Cancel
            </Button>
            {leaseMode === "simple" && (
              <Button color="success" onClick={handleSaveLease} disabled={leaseSaving}>
                {leaseSaving ? <><Spinner size="sm" className="me-1" />Saving...</> : editingLease ? "Save Changes" : "Save Payment Setup"}
              </Button>
            )}
          </ModalFooter>
        </Modal>

        {/* Record Manual Payment Modal */}
        <Modal isOpen={recordPaymentModal} toggle={() => { setRecordPaymentModal(false); setEditingPaymentId(null); }}>
          <ModalHeader toggle={() => { setRecordPaymentModal(false); setEditingPaymentId(null); }}>
            {editingPaymentId ? "Edit Payment" : "Record Payment"}
          </ModalHeader>
          <ModalBody>
            {recordPaymentError && <Alert color="danger" className="py-2 small">{recordPaymentError}</Alert>}
            <FormGroup>
              <Label>Tenant</Label>
              {leaseTenants.length > 1 ? (
                <Input
                  type="select"
                  value={recordPaymentForm.tenantId}
                  onChange={(e) => setRecordPaymentForm({ ...recordPaymentForm, tenantId: e.target.value })}
                >
                  <option value="">Select a tenant...</option>
                  {leaseTenants.map((t) => (
                    <option key={t.id} value={t.id}>{t.name || t.email}</option>
                  ))}
                </Input>
              ) : (
                <div className="border rounded p-2 bg-light small">
                  {leaseTenants[0]?.name || leaseTenants[0]?.email || "No tenant assigned to this lease"}
                </div>
              )}
            </FormGroup>
            <FormGroup>
              <Label>Payment type</Label>
              <Input
                type="select"
                value={recordPaymentForm.type}
                onChange={(e) => {
                  const type = e.target.value;
                  setRecordPaymentForm({
                    ...recordPaymentForm,
                    type,
                    amount: type === "deposit"
                      ? (activeLease?.securityDeposit ? String(activeLease.securityDeposit) : "")
                      : (activeLease?.monthlyRent ? String(activeLease.monthlyRent) : ""),
                  });
                }}
              >
                <option value="rent">Rent</option>
                <option value="deposit">Security Deposit</option>
              </Input>
            </FormGroup>
            <Row>
              {recordPaymentForm.type === "rent" && (
                <Col sm={6}>
                  <FormGroup>
                    <Label>Month covered</Label>
                    <MonthPicker
                      value={recordPaymentForm.month}
                      onChange={(ym) => setRecordPaymentForm({ ...recordPaymentForm, month: ym })}
                    />
                  </FormGroup>
                </Col>
              )}
              <Col sm={recordPaymentForm.type === "rent" ? 6 : 12}>
                <FormGroup>
                  <Label>Amount ($)</Label>
                  <Input
                    type="number"
                    min="0"
                    value={recordPaymentForm.amount}
                    onChange={(e) => setRecordPaymentForm({ ...recordPaymentForm, amount: e.target.value })}
                  />
                </FormGroup>
              </Col>
            </Row>
            <FormGroup>
              <Label>Paid on</Label>
              <Input
                type="date"
                value={recordPaymentForm.paidAt}
                onChange={(e) => setRecordPaymentForm({ ...recordPaymentForm, paidAt: e.target.value })}
              />
              <small className="text-muted">Can be a past date — e.g. to backfill a cash/check payment.</small>
            </FormGroup>
            <FormGroup className="mb-0">
              <Label>Note (optional)</Label>
              <Input
                type="text"
                placeholder="e.g. Paid by check #1234"
                value={recordPaymentForm.note}
                onChange={(e) => setRecordPaymentForm({ ...recordPaymentForm, note: e.target.value })}
              />
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" outline onClick={() => { setRecordPaymentModal(false); setEditingPaymentId(null); }}>Cancel</Button>
            <Button color="success" onClick={handleRecordPayment} disabled={recordPaymentSaving}>
              {recordPaymentSaving ? <><Spinner size="sm" className="me-1" />Saving...</> : editingPaymentId ? "Save Changes" : "Save Payment"}
            </Button>
          </ModalFooter>
        </Modal>

        {/* Delete Payment Confirm Modal */}
        <Modal isOpen={deletePaymentModal} toggle={() => setDeletePaymentModal(false)}>
          <ModalHeader toggle={() => setDeletePaymentModal(false)}>Delete Payment</ModalHeader>
          <ModalBody>
            Delete this {deletePaymentTarget ? paymentSummaryLabel(deletePaymentTarget) : ""} payment of $
            {(deletePaymentTarget?.amount || 0).toLocaleString()}? This can't be undone.
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" outline onClick={() => setDeletePaymentModal(false)}>Cancel</Button>
            <Button color="danger" onClick={handleDeletePayment} disabled={deletePaymentSaving}>
              {deletePaymentSaving ? <Spinner size="sm" /> : "Delete Payment"}
            </Button>
          </ModalFooter>
        </Modal>

        {/* Share Access Modal */}
        <Modal isOpen={accessModal} toggle={() => setAccessModal(false)}>
          <ModalHeader toggle={() => setAccessModal(false)}>
            Share This Property
          </ModalHeader>
          <ModalBody>
            {accessError && <Alert color="danger">{accessError}</Alert>}
            <p className="text-muted small">
              Enter the email address of the person you want to share this property with.
              Next time they sign in, it will appear under <strong>Shared with me</strong>.
            </p>
            <FormGroup>
              <Label>Email address <span className="text-danger">*</span></Label>
              <Input
                type="email"
                placeholder="name@example.com"
                value={accessEmail}
                autoFocus
                onChange={(e) => setAccessEmail(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") handleShareAccess(); }}
              />
            </FormGroup>
            <FormGroup className="mb-0">
              <Label>Access level</Label>
              <Input
                type="select"
                value={accessRole}
                onChange={(e) => setAccessRole(e.target.value)}
              >
                <option value={PROPERTY_ACCESS_VIEWER}>Viewer — can view the property</option>
                <option value={PROPERTY_ACCESS_EDITOR}>Editor — can also make changes</option>
              </Input>
              <small className="text-muted">
                {accessRole === PROPERTY_ACCESS_EDITOR
                  ? "Editors can update details, manage tenants and set up payments. They cannot delete the property or change who it's shared with."
                  : "Viewers can see the property, its tenants, leases and tickets, but cannot change anything."}
              </small>
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setAccessModal(false)}>Cancel</Button>
            <Button color="warning" disabled={!accessEmail.trim() || accessSaving} onClick={handleShareAccess}>
              {accessSaving ? <Spinner size="sm" /> : "Share Access"}
            </Button>
          </ModalFooter>
        </Modal>

        {/* Revoke Access Confirm Modal */}
        <Modal isOpen={revokeModal} toggle={() => setRevokeModal(false)}>
          <ModalHeader toggle={() => setRevokeModal(false)}>
            Remove Access
          </ModalHeader>
          <ModalBody>
            Remove access for <strong>{revokeTarget?.email}</strong>? They will no longer
            be able to see this property when they sign in.
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setRevokeModal(false)}>
              Cancel
            </Button>
            <Button
              color="danger"
              disabled={accessSaving}
              onClick={handleRevokeAccess}
            >
              {accessSaving ? <Spinner size="sm" /> : "Remove Access"}
            </Button>
          </ModalFooter>
        </Modal>

        {/* Delete Property Modal */}
        <Modal isOpen={deleteModal} toggle={() => setDeleteModal(false)}>
          <ModalHeader toggle={() => setDeleteModal(false)}>Delete Property</ModalHeader>
          <ModalBody>
            {deleteError && <Alert color="danger">{deleteError}</Alert>}
            <p>
              Delete <strong>{property.address}</strong>? This will unlink any tenants,
              payment setups, and application links from this property.
            </p>
            <p className="text-muted small mb-0">
              The property can be restored from the Properties list afterward.
            </p>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setDeleteModal(false)}>Cancel</Button>
            <Button color="danger" disabled={deleting} onClick={handleDelete}>
              {deleting ? <Spinner size="sm" /> : "Delete"}
            </Button>
          </ModalFooter>
        </Modal>

        {/* Already Set Up Modal */}
        <Modal isOpen={alreadySetupModal} toggle={() => setAlreadySetupModal(false)}>
          <ModalHeader toggle={() => setAlreadySetupModal(false)}>Payments Already Set Up</ModalHeader>
          <ModalBody>
            <p className="mb-0">
              This property already has a payment setup. Would you like to delete it and set up again?
            </p>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setAlreadySetupModal(false)}>Cancel</Button>
            <Button color="danger" disabled={resetupSaving} onClick={handleResetupPayments}>
              {resetupSaving ? <Spinner size="sm" /> : "Delete & Set Up Again"}
            </Button>
          </ModalFooter>
        </Modal>

        {/* Applications Modal */}
        <Modal isOpen={appModal} toggle={() => setAppModal(false)} size="xl">
          <ModalHeader toggle={() => setAppModal(false)}>
            {selectedListingId ? "Applications" : "Application Links"}
            {selectedListingId && (
              <Badge color="primary" pill className="ms-2">{appsForListing(selectedListingId).length}</Badge>
            )}
          </ModalHeader>
          <ModalBody>
            {listingError && (
              <Alert color="danger" className="py-2 small">
                <i className="bx bx-error-circle me-1" />{listingError}
              </Alert>
            )}

            {!selectedListingId ? (
              /* ── Link overview ─────────────────────────────────── */
              listingsLoading ? (
                <div className="text-center py-5"><Spinner color="primary" /></div>
              ) : listings.length === 0 ? (
                <div className="text-center py-5">
                  <i className="bx bx-link text-muted" style={{ fontSize: "2.5rem" }} />
                  <p className="text-muted mt-2 mb-1">No application links yet for this property.</p>
                  <p className="text-muted small mb-0">Create one to start collecting applications.</p>
                </div>
              ) : (
                <div className="d-flex flex-column gap-3">
                  {listings.map((l) => {
                    const count = appsForListing(l.id).length;
                    const isActive = l.active !== false;
                    return (
                      <div key={l.id} className="border rounded p-3">
                        <div className="d-flex align-items-start justify-content-between gap-3 flex-wrap">
                          <div className="min-w-0">
                            <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                              <strong>{l.title || property?.address || "Application link"}</strong>
                              <Badge color={isActive ? "success" : "secondary"}>{isActive ? "Active" : "Retired"}</Badge>
                              <Badge color={count > 0 ? "primary" : "light"} className={count > 0 ? "" : "text-dark border"} pill>
                                {count} {count === 1 ? "application" : "applications"}
                              </Badge>
                            </div>
                            <div className="text-muted small">
                              {l.monthlyRent ? `$${Number(l.monthlyRent).toLocaleString()}/mo` : ""}
                              {l.createdAt ? `${l.monthlyRent ? " · " : ""}Created ${l.createdAt.toLocaleDateString()}` : ""}
                            </div>
                          </div>
                          <div className="d-flex gap-2 flex-shrink-0">
                            <Button size="sm" color="secondary" outline onClick={() => { setAppModal(false); openEditListingModal(l); }}>
                              <i className="bx bx-edit-alt me-1" /> Edit
                            </Button>
                            <Button size="sm" color="primary" outline onClick={() => openListingApplications(l.id)}>
                              <i className="bx bx-user-check me-1" /> View Applications
                            </Button>
                          </div>
                        </div>

                        <div className="mt-2">
                          <small className="text-muted d-block mb-1 fw-semibold">Application URL</small>
                          <div className="d-flex align-items-center gap-2">
                            <Input readOnly value={applyLinkFor(l.id)} bsSize="sm" />
                            <Button size="sm" color="light" className="border" onClick={() => handleCopyLink(applyLinkFor(l.id))} title="Copy link">
                              <i className={`bx ${copiedLink === applyLinkFor(l.id) ? "bx-check" : "bx-copy"}`} />
                            </Button>
                            <Button size="sm" color="light" className="border" tag="a" href={applyLinkFor(l.id)} target="_blank" rel="noopener noreferrer" title="Open link">
                              <i className="bx bx-link-external" />
                            </Button>
                          </div>
                        </div>

                        <div className="d-flex align-items-center gap-2 mt-2 flex-wrap">
                          <Button
                            size="sm"
                            color={isActive ? "warning" : "success"}
                            outline
                            disabled={listingBusy === l.id}
                            onClick={() => handleToggleListingActive(l)}
                            title={isActive ? "Stop accepting new applicants" : "Start accepting applicants again"}
                          >
                            {listingBusy === l.id
                              ? <Spinner size="sm" />
                              : <><i className={`bx ${isActive ? "bx-pause-circle" : "bx-play-circle"} me-1`} />{isActive ? "Retire" : "Reactivate"}</>}
                          </Button>

                          {confirmDeleteListing === l.id ? (
                            <>
                              <span className="small text-muted">Delete this link permanently?</span>
                              <Button size="sm" color="danger" disabled={listingBusy === l.id} onClick={() => handleDeleteListing(l)}>
                                Yes, delete
                              </Button>
                              <Button size="sm" color="light" className="border" onClick={() => setConfirmDeleteListing("")}>
                                Cancel
                              </Button>
                            </>
                          ) : (
                            <Button
                              size="sm"
                              color="danger"
                              outline
                              disabled={count > 0}
                              title={count > 0 ? "This link has applications — retire it instead" : "Delete this link"}
                              onClick={() => { setListingError(""); setConfirmDeleteListing(l.id); }}
                            >
                              <i className="bx bx-trash me-1" /> Delete
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )
            ) : appsForListing(selectedListingId).length === 0 ? (
              <div className="text-center py-5">
                <i className="bx bx-user-check text-muted" style={{ fontSize: "2.5rem" }} />
                <p className="text-muted mt-2 mb-1">No applications yet for this link.</p>
                <p className="text-muted small mb-0">Share the link to start collecting applications.</p>
              </div>
            ) : (
              <Row className="g-3">
                {/* Applicant list */}
                <Col md={4}>
                  <Input
                    placeholder="Search by name, email or status…"
                    value={appSearch}
                    onChange={(e) => setAppSearch(e.target.value)}
                    bsSize="sm"
                    className="rounded mb-2"
                  />
                  <div className="d-flex flex-column gap-2 pe-1" style={{ maxHeight: 480, overflowY: "auto" }}>
                    {filteredApps.length === 0 ? (
                      <p className="text-muted small text-center py-3 mb-0">No applications match your search.</p>
                    ) : filteredApps.map((a) => (
                      <button
                        key={a.id}
                        type="button"
                        onClick={() => { setSelectedApp(a); setAppStatusError(""); }}
                        className={`text-start rounded p-2 ${selectedApp?.id === a.id ? "border border-primary" : "border"}`}
                        style={{ background: selectedApp?.id === a.id ? "#eef4ff" : "#fff" }}
                      >
                        <div className="d-flex align-items-center justify-content-between gap-2 mb-1">
                          <strong className="small text-truncate">{a.name}</strong>
                          <Badge
                            color={a.status === "approved" ? "success" : a.status === "rejected" ? "danger" : a.status === "reviewing" ? "warning" : "info"}
                            className="small flex-shrink-0"
                          >
                            {a.status === "new" ? "New" : a.status === "reviewing" ? "Reviewing" : a.status === "approved" ? "Approved" : "Rejected"}
                          </Badge>
                        </div>
                        <div className="text-muted small text-truncate">{a.email}</div>
                        <div className="d-flex align-items-center gap-2 text-muted" style={{ fontSize: "0.7rem" }}>
                          {a.submittedAt && <span>{a.submittedAt.toLocaleDateString()}</span>}
                          {a.additionalApplicants?.length > 0 && <span>+{a.additionalApplicants.length} more</span>}
                        </div>
                      </button>
                    ))}
                  </div>
                </Col>

                {/* Selected application detail */}
                <Col md={8} className="border-start">
                  {!selectedApp ? (
                    <p className="text-muted small mb-0">Select an application to view its details.</p>
                  ) : (
                    <>
              <Row className="g-3 mb-3">
                <Col md={6}>
                  <div className="bg-light rounded p-3">
                    <small className="text-muted d-block mb-1">Full Name</small>
                    <strong>{selectedApp.name}</strong>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="bg-light rounded p-3">
                    <small className="text-muted d-block mb-1">Email</small>
                    <strong>{selectedApp.email}</strong>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="bg-light rounded p-3">
                    <small className="text-muted d-block mb-1">Phone</small>
                    <strong>{selectedApp.phone || "—"}</strong>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="bg-light rounded p-3">
                    <small className="text-muted d-block mb-1">Employer</small>
                    <strong>{selectedApp.employer || "—"}</strong>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="bg-light rounded p-3">
                    <small className="text-muted d-block mb-1">Monthly Income</small>
                    <strong>{selectedApp.monthlyIncome ? `$${Number(selectedApp.monthlyIncome).toLocaleString()}` : "—"}</strong>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="bg-light rounded p-3">
                    <small className="text-muted d-block mb-1">Submitted</small>
                    <strong>{selectedApp.submittedAt ? selectedApp.submittedAt.toLocaleDateString() : "—"}</strong>
                  </div>
                </Col>
              </Row>

              {/* Screening report — same criminal/credit/eviction report as the Applications page */}
              <div className="mb-3">
                <small className="text-muted d-block mb-2 fw-semibold">Background Screening Report</small>
                <ScreeningReportPanel applicationId={selectedApp.id} />
              </div>

              {/* Additional applicants */}
              {selectedApp.additionalApplicants?.length > 0 && (
                <div className="border rounded p-3 mb-3">
                  <small className="text-muted d-block mb-2 fw-semibold">Additional Applicants</small>
                  {selectedApp.additionalApplicants.map((p, i) => (
                    <div key={i} className="d-flex gap-3 small mb-1">
                      <span className="fw-semibold">{p.name}</span>
                      {p.email && <span className="text-muted">{p.email}</span>}
                      {p.relationship && <Badge color="light" className="text-dark border">{p.relationship}</Badge>}
                    </div>
                  ))}
                </div>
              )}

              {selectedApp.message && (
                <div className="border rounded p-3 mb-3">
                  <small className="text-muted d-block mb-1">Message</small>
                  <p className="mb-0">{selectedApp.message}</p>
                </div>
              )}

              <div className="border-top pt-3">
                <small className="text-muted d-block mb-2 fw-semibold">Update Status</small>
                {(() => {
                  const approvedOther = applications.find(
                    (a) => a.id !== selectedApp.id && a.listingId === selectedApp.listingId && a.status === "approved"
                  );
                  return (
                    <>
                      {appStatusError && (
                        <Alert color="danger" className="py-2 small mb-2">
                          <i className="bx bx-error-circle me-1" />{appStatusError}
                        </Alert>
                      )}
                      {approvedOther && (
                        <Alert color="warning" className="py-2 small mb-2">
                          <i className="bx bx-info-circle me-1" />
                          <strong>{approvedOther.name || approvedOther.email}</strong> is already approved for this link.
                          Only one applicant can be approved at a time.
                        </Alert>
                      )}
                      <div className="d-flex gap-2 flex-wrap">
                        {[
                          { key: "reviewing", label: "Mark Reviewing", color: "warning" },
                          { key: "approved",  label: "Approve",        color: "success" },
                          { key: "rejected",  label: "Reject",         color: "danger" },
                        ].map(({ key, label, color }) => (
                          <Button
                            key={key}
                            size="sm"
                            color={color}
                            outline={selectedApp.status !== key}
                            disabled={key === "approved" && !!approvedOther}
                            title={key === "approved" && approvedOther ? "Another applicant is already approved for this link" : ""}
                            onClick={() => handleUpdateAppStatus(selectedApp.id, key)}
                          >
                            {selectedApp.status === key && <i className="bx bx-check me-1" />}
                            {label}
                          </Button>
                        ))}
                      </div>
                    </>
                  );
                })()}
              </div>

              {selectedApp.status === "approved" && (
                <div className="border-top pt-3 mt-3">
                  <small className="text-muted d-block mb-2 fw-semibold">Actions</small>
                  <Button
                    color="success"
                    outline
                    size="sm"
                    onClick={() => handleAddTenantFromApp(selectedApp)}
                  >
                    <i className="bx bx-user-plus me-1" /> Add as Tenant
                  </Button>
                </div>
              )}
                    </>
                  )}
                </Col>
              </Row>
            )}
          </ModalBody>
          <ModalFooter>
            {selectedListingId ? (
              <Button color="light" className="border" onClick={() => { setSelectedListingId(null); setSelectedApp(null); setAppStatusError(""); }}>
                <i className="bx bx-arrow-back me-1" /> Back to Links
              </Button>
            ) : (
              <>
                <Button
                  color="primary"
                  disabled={listingsLoading || listings.some((l) => l.active !== false)}
                  onClick={() => { setAppModal(false); openListingModal(); }}
                >
                  <i className="bx bx-plus me-1" /> Create New Link
                </Button>
                {listings.some((l) => l.active !== false) && (
                  <small className="text-muted me-auto ms-2">
                    Only one active application link can exist at a time.
                  </small>
                )}
              </>
            )}
            <Button color="secondary" onClick={() => setAppModal(false)}>Close</Button>
          </ModalFooter>
        </Modal>

        {/* Create Rental Application Link Modal */}
        <Modal isOpen={listingModal} toggle={() => setListingModal(false)} size="lg">
          <ModalHeader toggle={() => setListingModal(false)}>
            {editingListingId ? "Edit Application Link" : "Create Rental Application Link"}
          </ModalHeader>
          <ModalBody>
            {newListingId ? (
              <div className="text-center py-3">
                <div className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{ width: 64, height: 64, background: "#34c38f22" }}>
                  <i className="bx bx-check text-success" style={{ fontSize: "2rem" }} />
                </div>
                <h5>Application Link Ready</h5>
                <p className="text-muted small">
                  Share this link with prospective tenants. Submitted applications will appear under “View Applications”.
                </p>
                <small className="text-muted d-block text-start mb-1 fw-semibold">Application URL</small>
                <div className="d-flex align-items-center gap-2">
                  <Input readOnly value={applyLinkFor(newListingId)} bsSize="sm" />
                  <Button size="sm" color="primary" onClick={() => handleCopyLink(applyLinkFor(newListingId))}>
                    <i className={`bx ${copiedLink === applyLinkFor(newListingId) ? "bx-check" : "bx-copy"} me-1`} />
                    {copiedLink === applyLinkFor(newListingId) ? "Copied" : "Copy"}
                  </Button>
                  <Button size="sm" color="light" className="border" tag="a" href={applyLinkFor(newListingId)} target="_blank" rel="noopener noreferrer">
                    <i className="bx bx-link-external" />
                  </Button>
                </div>
              </div>
            ) : (
              <>
                {listingsLoading ? (
                  <div className="text-center py-2"><Spinner size="sm" color="primary" /></div>
                ) : (!editingListingId && listings.some((l) => l.active !== false)) && (
                  <Alert color="warning" className="py-2 small">
                    <i className="bx bx-info-circle me-1" />
                    This property already has an active application link. Retire it from{" "}
                    <button
                      type="button"
                      className="btn btn-link btn-sm p-0 align-baseline"
                      onClick={() => { setListingModal(false); openApplicationsModal(); }}
                    >
                      View Applications
                    </button>{" "}
                    before creating a new one.
                  </Alert>
                )}

                <p className="text-muted small">
                  Applicants sign in, then fill out their name, phone, employer, monthly income, a message,
                  and any additional applicants. The details below are shown to them on the application page.
                </p>

                <Row className="g-3">
                  <Col md={12}>
                    <FormGroup>
                      <Label className="small fw-semibold">Listing Title</Label>
                      <Input
                        value={listingForm.title}
                        maxLength={100}
                        onChange={(e) => setListingForm((f) => ({ ...f, title: e.target.value }))}
                        placeholder={getListingDisplayTitle({ address: property?.address, bedrooms: listingForm.bedrooms })}
                      />
                      <small className="text-muted">Optional. A title will be generated from the bedroom count and city if left blank.</small>
                    </FormGroup>
                  </Col>
                  <Col md={4}>
                    <FormGroup>
                      <Label className="small fw-semibold">Monthly Rent *</Label>
                      <Input
                        type="number"
                        min="0"
                        value={listingForm.monthlyRent}
                        onChange={(e) => setListingForm((f) => ({ ...f, monthlyRent: e.target.value }))}
                        placeholder="2400"
                      />
                    </FormGroup>
                  </Col>
                  <Col md={4}>
                    <FormGroup>
                      <Label className="small fw-semibold">Security Deposit</Label>
                      <Input
                        type="number"
                        min="0"
                        value={listingForm.deposit}
                        onChange={(e) => setListingForm((f) => ({ ...f, deposit: e.target.value }))}
                        placeholder="2400"
                      />
                    </FormGroup>
                  </Col>
                  <Col md={2}>
                    <FormGroup>
                      <Label className="small fw-semibold">Beds</Label>
                      <Input
                        type="number"
                        min="0"
                        value={listingForm.bedrooms}
                        onChange={(e) => setListingForm((f) => ({ ...f, bedrooms: e.target.value }))}
                        placeholder="3"
                      />
                    </FormGroup>
                  </Col>
                  <Col md={2}>
                    <FormGroup>
                      <Label className="small fw-semibold">Baths</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.5"
                        value={listingForm.bathrooms}
                        onChange={(e) => setListingForm((f) => ({ ...f, bathrooms: e.target.value }))}
                        placeholder="2"
                      />
                    </FormGroup>
                  </Col>
                  <Col md={3}>
                    <FormGroup>
                      <Label className="small fw-semibold">Square Feet</Label>
                      <Input
                        type="number"
                        min="0"
                        value={listingForm.sqft}
                        onChange={(e) => setListingForm((f) => ({ ...f, sqft: e.target.value }))}
                        placeholder="1,500"
                      />
                    </FormGroup>
                  </Col>
                  <Col md={3}>
                    <FormGroup>
                      <Label className="small fw-semibold">Available Date</Label>
                      <Input
                        type="date"
                        value={listingForm.availableDate}
                        onChange={(e) => setListingForm((f) => ({ ...f, availableDate: e.target.value }))}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={3}>
                    <FormGroup>
                      <Label className="small fw-semibold">Pets Allowed</Label>
                      <Input
                        type="select"
                        value={listingForm.petPolicy}
                        onChange={(e) => setListingForm((f) => ({ ...f, petPolicy: e.target.value }))}
                      >
                        <option value="no-pets">No</option>
                        <option value="pets-ok">Yes</option>
                      </Input>
                    </FormGroup>
                  </Col>
                  <Col md={3}>
                    <FormGroup>
                      <Label className="small fw-semibold">Lease Term</Label>
                      <Input
                        type="select"
                        value={listingForm.leaseTerm}
                        onChange={(e) => setListingForm((f) => ({ ...f, leaseTerm: e.target.value }))}
                      >
                        <option value="month-to-month">Month-to-Month</option>
                        <option value="6-months">6 Month</option>
                        <option value="12-months">1 Year</option>
                        <option value="24-months">2 Year</option>
                      </Input>
                    </FormGroup>
                  </Col>
                  <Col md={12}>
                    <FormGroup>
                      <Label className="small fw-semibold">Description</Label>
                      <Input
                        type="textarea"
                        rows={3}
                        value={listingForm.description}
                        onChange={(e) => setListingForm((f) => ({ ...f, description: e.target.value }))}
                        placeholder="Describe the property for applicants…"
                      />
                    </FormGroup>
                  </Col>
                  <Col md={12}>
                    <FormGroup className="mb-0">
                      <Label className="small fw-semibold">Photos</Label>
                      <p className="text-muted small mb-2">
                        Photo 1 is the cover. Drag photos to reorder them, or use the arrow controls.
                      </p>
                      <Input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={(e) => { handleAddListingImageFiles(e.target.files); e.target.value = ""; }}
                      />
                      <p className="text-muted small mt-2 mb-0">
                        By uploading, you agree to our{" "}
                        <a href="/community-guidelines" target="_blank" rel="noopener noreferrer">Community Guidelines</a>.
                      </p>
                      {listingImages.length > 0 && (
                        <div className="d-flex gap-3 flex-wrap mt-3">
                          {listingImages.map((image, index) => (
                            <div
                              key={image.id}
                              className="position-relative border rounded overflow-hidden bg-light"
                              style={{ width: 144, cursor: "grab" }}
                              draggable
                              onDragStart={(event) => event.dataTransfer.setData("text/plain", image.id)}
                              onDragOver={(event) => event.preventDefault()}
                              onDrop={(event) => {
                                event.preventDefault();
                                moveListingImage(event.dataTransfer.getData("text/plain"), index);
                              }}
                            >
                              <div style={{ height: 100, background: "#e9ecef" }}>
                                {image.previewUrl ? (
                                  <img src={image.previewUrl} alt={`Listing photo ${index + 1}`} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                                ) : (
                                  <div className="d-flex align-items-center justify-content-center h-100 text-muted">
                                    <Spinner size="sm" />
                                  </div>
                                )}
                              </div>
                              <span className="badge bg-dark position-absolute top-0 start-0 m-1">{index + 1}</span>
                              {index === 0 && (
                                <span className="badge bg-primary position-absolute top-0 start-50 translate-middle-x mt-1">Cover</span>
                              )}
                              <button
                                type="button"
                                className="btn btn-sm btn-danger position-absolute top-0 end-0 m-1 p-0 d-flex align-items-center justify-content-center"
                                style={{ width: 24, height: 24 }}
                                aria-label={`Remove photo ${index + 1}`}
                                title="Remove photo"
                                onClick={() => handleRemoveListingImage(image.id)}
                              >
                                <i className="bx bx-x" />
                              </button>
                              <div className="d-flex align-items-center justify-content-between p-1 border-top bg-white">
                                <button
                                  type="button"
                                  className="btn btn-sm btn-light p-0"
                                  style={{ width: 28, height: 28 }}
                                  disabled={index === 0}
                                  aria-label="Move photo left"
                                  title="Move left"
                                  onClick={() => moveListingImage(image.id, index - 1)}
                                >
                                  <i className="bx bx-chevron-left" />
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-link p-0"
                                style={{ lineHeight: 1.4 }}
                                  disabled={index === 0}
                                  title={index === 0 ? "Cover photo" : "Make cover"}
                                  onClick={() => moveListingImage(image.id, 0)}
                                >
                                  <i className={`bx ${index === 0 ? "bxs-star" : "bx-star"} me-1`} />
                                  {index === 0 ? "Cover" : "Make cover"}
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-light p-0"
                                  style={{ width: 28, height: 28 }}
                                  disabled={index === listingImages.length - 1}
                                  aria-label="Move photo right"
                                  title="Move right"
                                  onClick={() => moveListingImage(image.id, index + 1)}
                              >
                                  <i className="bx bx-chevron-right" />
                              </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </FormGroup>
                  </Col>
                </Row>

                {listingError && (
                  <Alert color="danger" className="py-2 small mb-0">
                    <i className="bx bx-error-circle me-1" />{listingError}
                  </Alert>
                )}
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setListingModal(false)}>Close</Button>
            {!newListingId && (
              <Button
                color="primary"
                onClick={editingListingId ? handleUpdateListing : handleCreateListing}
                disabled={listingSaving || listingImagesUploading || listingsLoading || (!editingListingId && listings.some((l) => l.active !== false))}
              >
                {listingSaving || listingImagesUploading
                  ? <><Spinner size="sm" className="me-1" />{listingImagesUploading ? "Uploading photos…" : editingListingId ? "Saving…" : "Creating…"}</>
                  : editingListingId ? "Save Changes" : "Create Link"}
              </Button>
            )}
          </ModalFooter>
        </Modal>


        {/* Remove Tenant Confirm Modal */}
        <Modal isOpen={removeTenantModal} toggle={() => setRemoveTenantModal(false)}>
          <ModalHeader toggle={() => setRemoveTenantModal(false)}>
            Unlink Tenant from Property
          </ModalHeader>
          <ModalBody>
            Remove <strong>{removeTenantTarget?.name}</strong> from this property?
            The tenant will not be deleted — only unlinked from this property.
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setRemoveTenantModal(false)}>
              Cancel
            </Button>
            <Button
              color="danger"
              disabled={tenantSaving}
              onClick={handleRemoveTenantFromProperty}
            >
              {tenantSaving ? <Spinner size="sm" /> : "Unlink"}
            </Button>
          </ModalFooter>
        </Modal>

      </Container>
    </div>
  );
};

export default PropertyDetail;
